<template>
  <Modal
    :buttons="buttons"
    :modal-style="{ width: '60vw', maxWidth: '60vw' }"
    :title="title"
  >
    <div class="card q-pa-lg column no-wrap full-height scroll">
      <QSpinner
        v-if="loading"
        class="absolute-center"
        size="50"
      />

      <div v-else>
        <div class="q-mb-md">
          <h2>Team</h2>
          <p>{{ organisationTeam ? organisationTeam.name : "No Team" }}</p>
          <div v-if="organisationTeam">
            <h2>Brief Sets in Team</h2>
            <div v-if="showBriefSetsInfo">
              <p
                v-for="briefSet in organisationTeam.briefSets"
                :key="briefSet.id"
                class="team-brief-sets"
              >
                {{ briefSet.name }}
              </p>
            </div>
            <div v-else>No Brief Sets in Team</div>
          </div>
        </div>

        <UserBriefsTable
          v-model:filters="briefFilters"
          :briefs="userBriefs"
          class="full-height"
          :loading="loading"
          :readonly="readonly"
          reverse-selection
          :user="user"
          @update="updateSelectedBriefs"
          @update:filters="debouncedSearch"
        />
      </div>
    </div>

    <QCardActions align="right">
      <QBtn
        color="primary"
        :disable="readonly"
        flat
        label="Cancel"
        no-caps
        @click="cancel"
      />
      <QBtn
        color="black"
        :disabled="readonly || !selectedBriefs.length"
        :label="buttonLabel"
        no-caps
        @click="openConfirmModal"
      />
    </QCardActions>
  </Modal>
</template>

<script>
import { debounce } from "lodash-es";

import Modal from "shared/components/core/modals/Modal.vue";
import { pluralize } from "shared/helpers/string";
import { OrganisationTeam } from "shared/resources";

import UserBriefsTable from "src/components/users/UserBriefsTable.vue";

export default {
  name: "UserBriefsModal",
  components: {
    Modal,
    UserBriefsTable,
  },
  props: {
    user: {
      type: Object,
      required: true,
    },
    readonly: {
      type: Boolean,
    },
  },
  emits: ["confirm", "cancel"],
  data() {
    return {
      buttons: [
        {
          label: "Apply",
          onClick: this.save,
        },
      ],
      selectedBriefs: [],
      userBriefs: null,
      organisationTeam: null,
      loading: true,
      debouncedSearch: debounce(this.loadUserBriefs, 500),
      briefFilters: {
        id: "",
        group: "",
        label: "",
        origin: "",
      },
    };
  },
  computed: {
    title() {
      return `${this.user.name}'s Briefs`;
    },
    buttonLabel() {
      return `Unassign ${this.selectedBriefs.length} ${pluralize(
        this.selectedBriefs.length,
        "brief"
      )} from user`;
    },
    showBriefSetsInfo() {
      return this.organisationTeam?.briefSetIds.length > 0;
    },
  },
  async mounted() {
    try {
      this.loading = true;
      await this.loadUserBriefs();
      await this.loadOrganisationTeam();
    } catch (error) {
      this.$error("Error loading briefs for user", error);
    } finally {
      this.loading = false;
    }
  },
  methods: {
    async loadUserBriefs() {
      this.userBriefs = (
        await this.$streemApiAdmin.get(`users/${this.user.id}/briefs`)
      ).data;
    },
    async loadOrganisationTeam() {
      if (!this.user.primaryOrganisationTeamId()) return;

      this.loading = true;

      try {
        this.organisationTeam = (
          await OrganisationTeam.includes(["briefSets"]).find(
            this.user.primaryOrganisationTeamId()
          )
        ).data;
      } catch (error) {
        this.$error("There was an error loading the Organisation Team", error);
      } finally {
        this.loading = false;
      }
    },
    updateSelectedBriefs(briefs) {
      this.selectedBriefs = briefs;
    },
    openConfirmModal() {
      const message = `Are you sure you want to<br /> remove ${
        this.selectedBriefs.length
      } ${pluralize(this.selectedBriefs.length, "brief")} <br /> from user ${
        this.user.name
      }?`;

      this.$dialog
        .confirm({
          title: "Are you sure?",
          message,
          theme: "danger",
          persistent: true,
          dialogMaxWidth: "280px",
        })
        .onOk(() => {
          this.apply();
        });
    },
    async apply() {
      try {
        this.$q.loading.show();

        const params = {
          brief_ids: this.selectedBriefs.map((brief) => brief.id),
        };

        await this.$streemApiAdmin.put(
          `users/${this.user.id}/unassign_briefs`,
          { params }
        );

        this.$emit("confirm");
      } catch (error) {
        this.$error("Unable to remove briefs from user", error);
      } finally {
        this.$q.loading.hide();
      }
    },
    cancel() {
      this.$emit("cancel");
    },
  },
};
</script>

<style lang="scss" scoped>
.team-brief-sets {
  margin: 0;
}
</style>
